import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import {
  takeUntil, filter, take, concatMap
} from 'rxjs/operators';

import { CONDITION_TYPES, VALIDATORS } from 'app/constants';
import { Customer, Condition } from 'graphql/models/generated';
import { ConditionTypeENUM } from 'models/condition.model';
import { BaseComponent } from 'shared/base/base.component';
import { CoreDataService } from 'shared/services/core-data.service';
import { PermissionsService } from 'shared/services/permissions.service';
import { StoreService } from 'shared/services/store.service';

@Component({
  selector: 'app-customer-conditions',
  templateUrl: './conditions.component.html',
  styleUrls: ['./conditions.component.scss']
})
export class CustomerConditionsComponent extends BaseComponent implements OnInit {
  @Input() canAutoSave = true;

  @Input() showDefaultConditions = true;

  @Input() useDefaultConditions = false;

  radioData = {
    conditionsTypes: CONDITION_TYPES
  };

  readonly conditionType = ConditionTypeENUM;

  formReady = false;

  form!: UntypedFormGroup;

  isDealer!: boolean;

  get conditionsForm(): UntypedFormGroup {
    return this.form.controls.conditions as UntypedFormGroup;
  }

  constructor(
    protected fb: UntypedFormBuilder,
    protected coreDataService: CoreDataService,
    private storeService: StoreService,
    private permissionsService: PermissionsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.storeService.setFormReady(this.formReady);

    combineLatest([
      this.storeService.isCurrentAccountDealer().pipe(take(1)),
      this.storeService.getCurrentCustomer().pipe(take(1))
    ])
      .subscribe(([isDealer, customer]: [boolean, Customer]) => {
        this.isDealer = isDealer;
        this.setupForm(customer || {});
      });
  }

  setupForm(customer: Customer): void {
    const useDefaultConditions = (customer.useCustomConditions !== undefined)
      ? !customer.useCustomConditions : this.useDefaultConditions;
    if (this.isDealer) {
      const conditions: Condition = customer.conditions || {} as Condition;

      this.form = this.fb.group({
        useDefaultConditions: [useDefaultConditions, { updateOn: 'change' }],
        conditions: this.fb.group({
          id: [conditions.id || ''],
          type: [
            conditions.type || this.conditionType.SURCHARGE,
            { validators: [Validators.required], updateOn: 'change' }
          ],
          discount: [
            conditions.discount || 0,
            [
              Validators.required,
              Validators.min(VALIDATORS.MIN.DISCOUNT),
              Validators.max(VALIDATORS.MAX.DISCOUNT)
            ]
          ],
          surcharge: [
            conditions.surcharge || 0,
            [
              Validators.required,
              Validators.min(VALIDATORS.MIN.SURCHARGE),
              Validators.max(VALIDATORS.MAX.SURCHARGE)
            ]
          ],
          plCustomerCode: [
            conditions.plCustomerCode || '',
            [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT_SHORT)]
          ],
          externalSystemId: [
            conditions.externalSystemId || '',
            [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT_SHORT)]
          ]
        })
      }, { updateOn: 'blur' });
    } else {
      this.form = this.fb.group({
        baseDiscount: [{ value: customer.baseDiscount || '', disabled: true }],
        surcharge: [{ value: customer.surcharge || '', disabled: true }],
        paymentConditions: [{ value: customer.paymentConditions || '', disabled: true }],
        vat: [customer.vat || '']
      }, { updateOn: 'blur' });
    }

    if (this.permissionsService.canUpdateConditions()) {
      if (this.isDealer) {
        this.toggleConditionsForm(useDefaultConditions);
        this.detectConditionsChanges();
      }

      this.autoSaveSetup(customer.id);
    } else {
      if (this.isDealer) {
        this.conditionsForm.disable({ emitEvent: false });
      }

      this.form.disable({ emitEvent: false });
    }

    this.formReady = true;
    this.storeService.setFormReady(this.formReady);
  }

  private autoSaveSetup(customerId: string | undefined): void {
    if (!this.canAutoSave || !customerId) return;

    this.form.valueChanges
      .pipe(
        filter(() => this.form.valid),
        concatMap((values) => this.coreDataService.updateCustomer(customerId, values)),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (customer) => {
          /* TODO: disable form? */
          this.setupForm(customer);
        },
        (err) => { console.error(err); }
      );
  }

  private toggleConditionsForm(useDefaultConditions: boolean): void {
    if (useDefaultConditions) {
      this.conditionsForm.disable({ emitEvent: false });
    } else {
      this.conditionsForm.enable({ emitEvent: false });
    }
  }

  private detectConditionsChanges(): void {
    this.form.controls.useDefaultConditions.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((checked: boolean) => { this.toggleConditionsForm(checked); });
  }
}
